import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import ActivateProfileNotice from '@/modules/users/client/components/ActivateProfileNotice.component';
import Avatar from '@/modules/users/client/components/Avatar.component';
import { fetchMini } from '@/modules/users/client/api/users.api';
import * as contactsApi from '@/modules/contacts/client/api/contacts.api';
import { getCurrentRouteParams } from '@/modules/core/client/services/client-runtime';
import TrEditor from '@/modules/core/client/components/TrEditor';
import LoadingIndicator from '@/modules/core/client/components/LoadingIndicator';
import type { UserProfile } from '@/modules/users/client/types';
import { readApiError } from '@/modules/users/client/utils/api-error';

function defaultContactMessage(displayName: string): string {
  return `<p>Hi!</p><p>I would like to add you as a contact.</p><p>- ${displayName}</p>`;
}

export default function ContactAddPage({ user }: { user: UserProfile }) {
  const { t } = useTranslation('contacts') as {
    t: (key: string, options?: Record<string, unknown>) => string;
  };
  const { userId } = getCurrentRouteParams();
  const [message, setMessage] = useState(
    defaultContactMessage(user.displayName),
  );
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [wasConnected, setWasConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const isSelf = userId === user._id;
  const friendQuery = useQuery({
    queryKey: ['contact-add-friend', userId],
    queryFn: () => fetchMini(userId),
    enabled: user.public && !isSelf,
    refetchOnWindowFocus: false,
    retry: false,
  });
  const friend = friendQuery.data;
  const contactQuery = useQuery({
    queryKey: ['contact-add-existing', userId],
    queryFn: () => contactsApi.getByUserId(userId),
    enabled: user.public && !isSelf && Boolean(friend),
    refetchOnWindowFocus: false,
    retry: false,
  });
  const existingContact = contactQuery.data;
  const existingContactMessage = existingContact
    ? existingContact.confirmed
      ? (t('You two are already connected. Great!') as string)
      : (t(
          'Connection already initiated; now it has to be confirmed.',
        ) as string)
    : '';
  const displayedError =
    error ||
    (isSelf
      ? (t('You cannot connect with yourself. That is just silly!') as string)
      : friendQuery.isError || contactQuery.isError
        ? (t('User does not exist.') as string)
        : '');
  const displayedSuccess = success || existingContactMessage;
  const isConnected =
    wasConnected ||
    isSelf ||
    friendQuery.isError ||
    contactQuery.isError ||
    Boolean(existingContact);
  const isFetching = friendQuery.isLoading || contactQuery.isLoading;

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      await contactsApi.create({
        friendUserId: userId,
        message,
      });
      setSuccess(
        t(
          'Done! We sent an email to your contact and he/she still needs to confirm it.',
        ) as string,
      );
      setWasConnected(true);
    } catch (requestError: unknown) {
      const apiError = readApiError(requestError);
      if (apiError.status === 409) {
        setSuccess(
          apiError.data.confirmed === true
            ? (t('You two are already connected. Great!') as string)
            : (t(
                'Connection already initiated; now it has to be confirmed.',
              ) as string),
        );
        setWasConnected(true);
      } else {
        const { message } = apiError;
        setError(message || (t('Something went wrong. Try again.') as string));
      }
    } finally {
      setIsLoading(false);
    }
  }

  if (!user.public) {
    return (
      <section className="container container-spacer">
        <ActivateProfileNotice />
      </section>
    );
  }

  return (
    <section className="container container-spacer">
      <form onSubmit={handleSubmit} autoComplete="off">
        <div className="row">
          <div className="col-xs-12 col-sm-offset-1 col-sm-10 col-md-offset-2 col-md-8 col-lg-offset-3 col-lg-6">
            <div className="page-header">
              <h2>{t('Add contact') as string}</h2>
            </div>

            {isFetching && <LoadingIndicator />}

            {!isFetching && friend?._id && (
              <div className="panel panel-default contacts-connection">
                <div className="panel-body">
                  <div className="row">
                    <div className="col-xs-12 col-sm-5 contacts-connection-profile text-center">
                      <Avatar size={128} user={user} link={false} />
                      <h4 className="contacts-connection-name">
                        {user.displayName}
                      </h4>
                    </div>
                    <div className="hidden-xs col-sm-2">
                      <i className="icon-exchange icon-3x text-muted contacts-connection-divider" />
                    </div>
                    <div className="col-xs-12 col-sm-5 contacts-connection-profile text-center">
                      <Avatar size={128} user={friend} link={false} />
                      <h4 className="contacts-connection-name">
                        {friend.displayName}
                      </h4>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {!isConnected && friend?._id && (
              <div className="alert alert-info" role="note">
                {t(
                  "You don't need to add someone as a contact to message them. Only add someone as a contact if you have met them or plan to meet them.",
                )}
              </div>
            )}

            {!isConnected && friend?._id && (
              <div className="panel panel-default">
                <div className="panel-heading">
                  <h4>
                    {
                      t('Edit message for {{name}}:', {
                        name: friend.displayName,
                      }) as string
                    }
                  </h4>
                </div>
                <div className="panel-body">
                  <div className="contact-message">
                    <TrEditor
                      id="contact-message"
                      onChange={setMessage}
                      text={message}
                    />
                  </div>
                </div>
              </div>
            )}

            {displayedSuccess && (
              <div className="alert alert-success">
                <h4>{displayedSuccess}</h4>
                <p>
                  <strong>
                    <a href={`/profile/${user.username}`}>{user.displayName}</a>
                  </strong>{' '}
                  &amp;{' '}
                  <strong>
                    <a href={`/profile/${friend?.username}`}>
                      {friend?.displayName}
                    </a>
                  </strong>
                  .
                </p>
              </div>
            )}
            {displayedError && (
              <div className="alert alert-danger text-center">
                <strong>{displayedError}</strong>
              </div>
            )}

            {!isConnected && friend?._id && (
              <p className="text-center">
                <button
                  type="submit"
                  className="btn btn-lg btn-primary"
                  disabled={isLoading}
                >
                  {isLoading
                    ? (t('Wait a moment…') as string)
                    : (t('Add contact') as string)}
                </button>
                <br />
                <br />
                <a href={`/profile/${friend.username}`} className="text-muted">
                  {t('Cancel') as string}
                </a>
              </p>
            )}
          </div>
        </div>
      </form>
    </section>
  );
}
